
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
// Corrected: Removed fetchCategories import from api.ts
import { fetchProducts /*, deleteProduct */ } from '@/lib/api'; 
// Added: Import getCategories from categories.ts
import { getCategories } from '@/lib/categories';
// Added: Import Category type
import { Category } from '@/types/category'; 
// Import the mapper and type from types.ts
import { mapProductFromAPI, Product as FrontendProduct } from '@/lib/types'; 
import { toast } from 'sonner';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger, 
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge'; 
import { Trash2, Edit, MoreVertical, Search, Plus, Eye, EyeOff, Loader2 } from 'lucide-react';
import AdminProtectedRoute from '@/components/admin/AdminProtectedRoute';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImageHandler from '@/components/ImageHandler';

const ProductsList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // Fetch categories using the correct function and type
  const { data: categories = [], isLoading: isCategoriesLoading } = useQuery<Category[], Error>({
    queryKey: ["admin-categories"], // Use a distinct key for admin if needed
    queryFn: getCategories, // Use the function from categories.ts
  });

  // Fetch products based on filters
  const { data: productsFromAPI, isLoading } = useQuery<any[], Error>({
    queryKey: ['admin-products', statusFilter, selectedCategory, searchTerm],
    queryFn: async () => {
      let apiStatusFilter: boolean | undefined = undefined;
      if (statusFilter === 'active') apiStatusFilter = true;
      if (statusFilter === 'inactive') apiStatusFilter = false;
      
      return await fetchProducts({ 
        includeInactive: statusFilter === 'all', 
        status: apiStatusFilter, 
        category: selectedCategory,
        search: searchTerm
      }); 
    },
    keepPreviousData: true, 
  });

  // Map API data to frontend type
  const products: FrontendProduct[] = React.useMemo(() => {
    const mapped = productsFromAPI ? productsFromAPI.map(mapProductFromAPI) : [];
    // Debug: verificar produtos sem imagem
    mapped.forEach(p => {
      if (!p.imageUrl) {
        console.warn(`Produto sem imageUrl: ${p.name} (ID: ${p.id})`);
      }
    });
    return mapped;
  }, [productsFromAPI]);
  
  // Delete Mutation (assuming deleteProduct function exists)
  const deleteMutation = useMutation<any, Error, string>({
    // mutationFn: deleteProduct, // Replace with actual delete function when available
    mutationFn: async (productId: string) => { 
        console.warn("Placeholder: Delete Product function not implemented in API");
        // Example: Call Supabase directly if not in api.ts
        // const { error } = await supabase.from('products').delete().eq('id', productId);
        // if (error) throw error;
        await new Promise(resolve => setTimeout(resolve, 500)); // Simulate delay
        // throw new Error("Simulated deletion error"); // Uncomment to test error case
        return { success: true }; // Simulate success
    }, 
    onSuccess: () => {
      toast.success('Produto excluído com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['admin-products'] });
    },
    onError: (error) => {
      toast.error(`Erro ao excluir produto: ${error.message}`);
    },
  });

  const handleDelete = (productId: string) => {
    if (window.confirm('Tem certeza que deseja excluir este produto?')) {
      deleteMutation.mutate(productId);
    }
  };

  return (
    <AdminProtectedRoute>
      <div className="container mx-auto py-8 px-4 md:px-6">
        <Card>
          <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <CardTitle className="text-2xl font-bold">Produtos</CardTitle>
              <CardDescription>Gerencie os produtos cadastrados na sua loja.</CardDescription>
            </div>
            <Link to="/admin/products/new">
              <Button size="sm">
                <Plus className="mr-2 h-4 w-4" />
                Adicionar Produto
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {/* Filter Section */}
            <div className="mb-6 flex flex-col sm:flex-row items-center gap-4">
              <div className="relative flex-grow w-full sm:w-auto">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  placeholder="Buscar por nome ou descrição..."
                  className="pl-9 w-full"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Select 
                value={selectedCategory ?? ''} 
                onValueChange={(value) => setSelectedCategory(value || null)} 
              >
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Filtrar Categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Todas as Categorias</SelectItem>
                  {isCategoriesLoading ? (
                     <SelectItem value="loading" disabled>Carregando...</SelectItem>
                  ) : (
                    // Use the Category type here
                    categories.map((category: Category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <Select 
                 value={statusFilter}
                 onValueChange={(value: 'all' | 'active' | 'inactive') => setStatusFilter(value)}
              >
                <SelectTrigger className="w-full sm:w-[150px]">
                  <SelectValue placeholder="Filtrar Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos Status</SelectItem>
                  <SelectItem value="active">Ativos</SelectItem>
                  <SelectItem value="inactive">Inativos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Products Table */}
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px]">Imagem</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Preço</TableHead>
                    <TableHead>Estoque</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="h-24 text-center">
                        <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" />
                        <span className="mt-2 block">Carregando produtos...</span>
                      </TableCell>
                    </TableRow>
                  ) : products.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="h-24 text-center text-gray-500">
                        Nenhum produto encontrado com os filtros aplicados.
                      </TableCell>
                    </TableRow>
                  ) : (
                    products.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell>
                          <ImageHandler 
                            src={product.imageUrl || '/placeholder.svg'} 
                            alt={product.name}
                            size="thumbnail"
                            className="h-16 w-16 rounded-md object-cover border"
                            onError={() => console.warn(`Erro ao carregar imagem do produto: ${product.name} (${product.id})`)}
                          />
                        </TableCell>
                        <TableCell className="font-medium max-w-[200px] truncate" title={product.name}>
                          {product.name}
                        </TableCell>
                        <TableCell>
                           <Badge variant={product.status ? 'success' : 'secondary'}>
                              {product.status ? 
                                <><Eye className="mr-1 h-3 w-3"/> Ativo</> : 
                                <><EyeOff className="mr-1 h-3 w-3"/> Inativo</>
                              }
                           </Badge>
                        </TableCell>
                        <TableCell>
                          {product.price.toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          })}
                        </TableCell>
                        <TableCell>
                          <Badge variant={product.stock > 0 ? 'outline' : 'destructive'}>
                            {product.stock}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/admin/products/${product.id}`)}>
                                <Edit className="mr-2 h-4 w-4" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => handleDelete(product.id)}
                                className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                disabled={deleteMutation.isPending} 
                              >
                                {deleteMutation.isPending && deleteMutation.variables === product.id ? (
                                   <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 
                                ) : (
                                   <Trash2 className="mr-2 h-4 w-4" />
                                )}
                                Excluir
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            {!isLoading && (
                 <div className="mt-4 text-sm text-gray-500">
                   Mostrando {products.length} produto(s).
                 </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminProtectedRoute>
  );
};

export default ProductsList;
